import {
  CreatePassport,
  PASSPORT_DAYS,
  PASSPORT_MAX_DOCUMENTS,
  PASSPORT_STATE_LABEL,
  consentText,
  passportExpiry,
  type Passport,
  type PassportDays,
} from '@cyberscore/contracts';
import { BookUser, Eye, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import type { AssessmentSummary } from '../assessments/api';
import { useBusiness } from '../business/context';
import { CardHead } from '../business/parts';
import { CopyRow } from '../business/ShareScore';
import { NoAssessment } from '../business/states';
import { useDocuments } from '../documents/api';
import { passportUrl, usePassportActions, usePassports } from './api';

const day = (iso: string) => new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur' }); // prettier-ignore
const TONE = { live: 'ok', expired: '', revoked: 'bad' } as const;

/**
 * The cyber passport (task 76): the business shares its score, the protections behind it and chosen
 * documents with another insurer, a broker or a customer, until a date it picks. The owner shares and stops
 * sharing; everyone else on the business sees what is shared and who opened it.
 */
export function PassportPage() {
  const { loading, current: a } = useBusiness();
  const { tenant } = useSession();
  const owner = can(tenant, 'sharing.create');
  const list = usePassports(a?.id ?? null);
  const [adding, setAdding] = useState(false);
  const [made, setMade] = useState<Passport | null>(null);
  if (loading) return <div className="empty">Loading…</div>;
  if (!a) return <NoAssessment />;
  const packs = list.data ?? [];

  return (
    <>
      <div className="bpage-head">
        <div>
          <h1>Cyber passport</h1>
          <p>Share your score, the protections behind it and your documents with another insurer, a broker or a customer, for as long as you choose. They see what you agreed to share, as it is today.</p>
        </div>
        {owner && !adding && (
          <div className="head-actions">
            <button type="button" className="bbtn pri" onClick={() => { setAdding(true); setMade(null); }}><Plus size={15} /> New share pack</button>
          </div>
        )}
      </div>

      {made && (
        <section className="bcard mt-l passport-made" aria-label="Your new link">
          <CardHead tone="ok" icon={<BookUser size={18} />} title={`Shared with ${made.audience}`} sub={`Until ${day(made.expires_at)}. Send them this link; you can stop sharing at any time.`} />
          <CopyRow id="passport-link" label="Link" value={passportUrl(made.token)} what="Link" />
        </section>
      )}
      {adding && <NewPack a={a} onDone={(p) => { setAdding(false); setMade(p); }} onCancel={() => setAdding(false)} />}

      <section className="bcard mt-l" aria-labelledby="packs-title">
        <CardHead icon={<BookUser size={18} />} title={<span id="packs-title">Share packs</span>} sub={owner ? 'Each one is a separate link for one audience. Stop one, and only that link stops working.' : 'What the owner has shared. Only the owner can share or stop sharing.'} />
        {list.isPending ? <p className="muted">Loading…</p> : !packs.length ? (
          <p className="muted">Nothing shared yet.{owner ? ' A share pack is a link for one audience, open until a date you pick.' : ''}</p>
        ) : (
          <ul className="passport-list">{packs.map((p) => <PackRow key={p.id} a={a} p={p} owner={owner} />)}</ul>
        )}
      </section>
    </>
  ); // prettier-ignore
}

function PackRow({ a, p, owner }: { a: AssessmentSummary; p: Passport; owner: boolean }) {
  const toast = useToast();
  const { revoke } = usePassportActions(a.id);
  const stop = async () => {
    try {
      await revoke.mutateAsync(p.id);
      toast(`Stopped sharing with ${p.audience}.`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not stop sharing', 'bad');
    }
  };
  return (
    <li className="passport-row" aria-label={p.audience}>
      <div className="row-between">
        <div className="minw">
          <b>{p.audience}</b>{p.audience_email && <span className="muted small"> · {p.audience_email}</span>}
          {p.purpose && <div className="muted small">{p.purpose}</div>}
        </div>
        <span className={`bbadge ${TONE[p.state]}`}>{PASSPORT_STATE_LABEL[p.state]}</span>
      </div>
      <div className="muted small passport-facts">
        <span>Score {p.score}</span>
        <span>{p.protections ? 'With protections' : 'Score only'}</span>
        <span>{p.documents.length ? `${p.documents.length} document${p.documents.length === 1 ? '' : 's'}` : 'No documents'}</span>
        <span>{p.state === 'revoked' ? `Stopped ${day(p.revoked_at!)}` : p.state === 'expired' ? `Ended ${day(p.expires_at)}` : `Until ${day(p.expires_at)}`}</span>
        <span><Eye size={12} aria-hidden /> {p.views ? `Opened ${p.views} time${p.views === 1 ? '' : 's'}, last ${day(p.last_viewed_at!)}` : 'Not opened yet'}</span>
      </div>
      {p.state === 'live' && (
        <div className="passport-live">
          <div className="grow"><CopyRow id={`pl-${p.id}`} label="Link" value={passportUrl(p.token)} what="Link" /></div>
          {owner && <button type="button" className="bbtn sm soft" disabled={revoke.isPending} onClick={stop}>Stop sharing</button>}
        </div>
      )}
      <details className="small muted mt-s"><summary>What {p.created_by_name ?? 'the owner'} agreed to</summary><p>{p.consent_text}</p></details>
    </li>
  ); // prettier-ignore
}

function NewPack({
  a,
  onDone,
  onCancel,
}: {
  a: AssessmentSummary;
  onDone: (p: Passport) => void;
  onCancel: () => void;
}) {
  const { create } = usePassportActions(a.id);
  const docs = useDocuments(a.id);
  const stored = (docs.data ?? []).filter((d) => d.status === 'stored');
  const [audience, setAudience] = useState('');
  const [email, setEmail] = useState('');
  const [purpose, setPurpose] = useState('');
  const [days, setDays] = useState<PassportDays>(30);
  const [protections, setProtections] = useState(true);
  const [picked, setPicked] = useState<string[]>([]);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const words = consentText({ business: a.customer_name, audience: audience.trim() || 'them', until: passportExpiry(new Date(), days).toISOString(), protections, documents: picked.length }); // prettier-ignore

  const submit = async () => {
    setBanner(null);
    const body = {
      audience,
      audience_email: email.trim() || null,
      purpose,
      days,
      protections,
      documents: picked,
      consent,
    };
    const parsed = CreatePassport.safeParse(body);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    try {
      onDone(await create.mutateAsync(body as CreatePassport));
    } catch (e) {
      setBanner(e instanceof ApiError ? (e.issues[0]?.message ?? e.message) : 'Could not share. Try again.');
    }
  };

  return (
    <section className="bcard mt-l passport-new" aria-labelledby="new-pack-title">
      <CardHead tone="acc" icon={<Plus size={18} />} title={<span id="new-pack-title">New share pack</span>} sub="What you pick is fixed when you share it. Share again later to send something newer." />
      {banner && <div className="result bad" role="alert">{banner}</div>}
      <div className="passport-form">
        <label htmlFor="pp-aud">Who it is for</label>
        <input id="pp-aud" className="input" maxLength={120} placeholder="For example: Kinta Steel procurement" value={audience} onChange={(e) => setAudience(e.target.value)} aria-invalid={!!errors.audience} />
        {errors.audience && <span className="bad-t small">{errors.audience}</span>}
        <label htmlFor="pp-email">Their email (optional, for your records)</label>
        <input id="pp-email" className="input" type="email" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!errors.audience_email} />
        {errors.audience_email && <span className="bad-t small">{errors.audience_email}</span>}
        <label htmlFor="pp-why">Why you are sharing it (optional)</label>
        <input id="pp-why" className="input" maxLength={500} placeholder="For example: supplier security review, 2027 tender" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        <span className="lbl-like">How long</span>
        <div className="bseg" role="group" aria-label="How long">
          {PASSPORT_DAYS.map((d) => <button key={d} type="button" aria-selected={days === d} onClick={() => setDays(d)}>{d} days</button>)}
        </div>
        <span className="lbl-like">What to share</span>
        <label className="toggle-row"><input type="checkbox" checked disabled /> <span>Your CyberScore, its band and how much is verified</span></label>
        <label className="toggle-row"><input type="checkbox" checked={protections} onChange={(e) => setProtections(e.target.checked)} /> <span>The protections behind it, and how each is backed</span></label>
        {stored.length > 0 && (
          <fieldset className="passport-docs"><legend>Documents (up to {PASSPORT_MAX_DOCUMENTS})</legend>
            {stored.map((d) => <label key={d.id} className="toggle-row small"><input type="checkbox" checked={picked.includes(d.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, d.id] : picked.filter((x) => x !== d.id))} /> <span>{d.file_name}</span></label>)}
          </fieldset>
        )}
        {errors.documents && <span className="bad-t small">{errors.documents}</span>}
        <label className="toggle-row passport-consent"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} aria-invalid={!!errors.consent} /> <span>{words}</span></label>
        {errors.consent && <span className="bad-t small">{errors.consent}</span>}
      </div>
      <div className="row gap-s mt">
        <button type="button" className="bbtn pri" disabled={create.isPending} onClick={submit}>{create.isPending && <Loader2 size={14} className="spin" />}Share</button>
        <button type="button" className="bbtn soft" onClick={onCancel}>Cancel</button>
      </div>
    </section>
  ); // prettier-ignore
}
